import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { API_URL } from '../../../utils/utils.js';
import '../../../styles/Auth/public-legal.css';

const PublicLegalPage = ({ docType: initialDocType }) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Determine target document type from prop or URL route
    const getTargetType = () => {
        if (initialDocType) return initialDocType;
        if (location.pathname.includes('terms-and-conditions')) {
            return 'TERMS_AND_CONDITIONS';
        }
        return 'PRIVACY_POLICY';
    };

    const targetType = getTargetType();
    const [loading, setLoading] = useState(true);
    const [docData, setDocData] = useState(null);
    const [error, setError] = useState(null);

    const defaultTitle = targetType === 'PRIVACY_POLICY' ? 'Privacy Policy' : 'Terms & Conditions';

    const fetchDocument = async () => {
        setLoading(true);
        setError(null);
        try {
            const apiPath = targetType === 'PRIVACY_POLICY' ? 'privacy-policy' : 'terms-and-conditions';
            const baseUrl = API_URL ? API_URL.replace(/\/+$/, '') : '';
            const fetchUrl = `${baseUrl}/legal-documents/public/${apiPath}`;

            const response = await fetch(fetchUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const resData = await response.json();
                if (resData && (resData.status === 200 || resData.success !== false) && resData.data) {
                    setDocData(resData.data);
                } else {
                    setDocData({
                        title: defaultTitle,
                        content: '<p>The requested document is currently not available. Please check back later.</p>'
                    });
                }
            } else {
                setDocData({
                    title: defaultTitle,
                    content: '<p>The requested document is currently not available. Please check back later.</p>'
                });
            }
        } catch (err) {
            console.error('Error loading public legal document:', err);
            setDocData({
                title: defaultTitle,
                content: '<p>Unable to load document contents at this time. Please try refreshing the page.</p>'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocument();
        window.scrollTo(0, 0);
    }, [targetType, location.pathname]);

    const handleBack = () => {
        if (window.history.length > 2) {
            navigate(-1);
        } else {
            navigate('/login');
        }
    };

    const formattedEffectiveDate = docData?.effectiveDate
        ? new Date(docData.effectiveDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
        : null;

    const formattedPublishedDate = docData?.publishedAt
        ? new Date(docData.publishedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
        : null;

    return (
        <div className="public-legal-wrapper">
            <div className="public-legal-container">
                {/* Header */}
                <div className="public-legal-header">
                    <div className="public-legal-nav-bar">
                        <button
                            type="button"
                            className="public-legal-back-btn"
                            onClick={handleBack}
                            aria-label="Go Back"
                        >
                            <Icon icon="mdi:arrow-left" width="20" height="20" />
                            <span>Back</span>
                        </button>

                        <div className="public-legal-brand">
                            <img src="/assets/logo.png" alt="TVFM Logo" onError={(e) => e.target.style.display = 'none'} />
                            <span>TVFM POS</span>
                        </div>
                    </div>

                    <div className="public-legal-header-content">
                        <h1 className="public-legal-title">
                            {docData?.title || defaultTitle}
                        </h1>

                        {!loading && !error && (
                            <div className="public-legal-meta">
                                {docData?.version && (
                                    <span className="public-legal-meta-badge">
                                        Version {docData.version}
                                    </span>
                                )}
                                {formattedEffectiveDate && (
                                    <>
                                        {docData?.version && <span className="public-legal-meta-divider">•</span>}
                                        <span>Effective: {formattedEffectiveDate}</span>
                                    </>
                                )}
                                {formattedPublishedDate && (
                                    <>
                                        <span className="public-legal-meta-divider">•</span>
                                        <span>Published: {formattedPublishedDate}</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Body Content */}
                <div className="public-legal-body">
                    {loading ? (
                        <div className="public-legal-inline-loader">
                            <div className="public-legal-spinner"></div>
                            <p>Loading document contents...</p>
                        </div>
                    ) : error ? (
                        <div className="public-legal-error">
                            <Icon icon="mdi:alert-circle-outline" className="public-legal-error-icon" />
                            <h2 className="public-legal-error-title">Unable to Display Document</h2>
                            <p>{error}</p>
                            <button
                                type="button"
                                className="public-legal-retry-btn"
                                onClick={fetchDocument}
                            >
                                Try Again
                            </button>
                        </div>
                    ) : (
                        <div
                            className="public-legal-content"
                            dangerouslySetInnerHTML={{
                                __html: docData?.content || '<p>No content available at this time.</p>'
                            }}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="public-legal-footer">
                    <div>
                        © {new Date().getFullYear()} TVFM POS. All rights reserved.
                    </div>
                    <div className="public-legal-footer-links">
                        {targetType === 'PRIVACY_POLICY' ? (
                            <span
                                className="public-legal-footer-link"
                                onClick={() => navigate('/terms-and-conditions')}
                            >
                                Terms & Conditions
                            </span>
                        ) : (
                            <span
                                className="public-legal-footer-link"
                                onClick={() => navigate('/privacy-policy')}
                            >
                                Privacy Policy
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicLegalPage;
